import { tUpdateAndAddToReturnArgs } from '../contexts/zod';

export type ReasonQuestionType = 'Text' | 'Select' | 'Checkbox' | 'DatePicker';

// Text is a string, Select an option id, Checkbox a boolean and DatePicker "yyyy-MM-dd".
export type ReasonQuestionValue = string | number | boolean;

export interface ReasonQuestionOption {
  id: number;
  value: string;
  label: string;
}

export interface ReasonQuestion {
  id: number;
  type: ReasonQuestionType;
  key: string;
  label: string;
  description: string | null;
  required: boolean;
  sortOrder: number;
  defaultValue: ReasonQuestionValue | null;
  maxLength: number | null;
  options: ReasonQuestionOption[];
}

export interface ReasonQuestionAnswer {
  reasonQuestionId: number;
  // Null when left empty, which the server reads as unanswered.
  value: ReasonQuestionValue | null;
}

export type ReasonQuestionValues = { [questionId: number]: ReasonQuestionValue | null };

export type ReasonQuestionError = 'required' | 'mustBeChecked' | 'maxLength' | 'invalid';

export const isEmptyValue = (value: ReasonQuestionValue | null | undefined): boolean => {
  return value === null || value === undefined || (typeof value === 'string' && value.trim() === '');
};

// A saved answer, even an emptied one, else the default; a Checkbox is unchecked unless its default says otherwise.
export const initialValue = (
  question: ReasonQuestion,
  answers: ReasonQuestionAnswer[] | undefined,
): ReasonQuestionValue | null => {
  const saved = (answers ?? []).find((answer) => {
    return answer.reasonQuestionId === question.id;
  });
  if (saved) {
    return saved.value;
  }
  if (question.type === 'Checkbox') {
    return question.defaultValue === true;
  }
  return question.defaultValue ?? null;
};

// The same checks the server makes, so its English messages are only a fallback.
export const validate = (question: ReasonQuestion, value: ReasonQuestionValue | null): ReasonQuestionError | null => {
  if (question.type === 'Checkbox') {
    return question.required && value !== true ? 'mustBeChecked' : null;
  }
  if (isEmptyValue(value)) {
    return question.required ? 'required' : null;
  }
  if (question.type === 'Text') {
    return question.maxLength && String(value).length > question.maxLength ? 'maxLength' : null;
  }
  if (question.type === 'Select') {
    return question.options.some((option) => {
      return option.id === value;
    })
      ? null
      : 'invalid';
  }
  if (question.type === 'DatePicker') {
    return /^\d{4}-\d{2}-\d{2}$/.test(String(value)) ? null : 'invalid';
  }
  return null;
};

// An emptied answer stays as null so an edit does not fill the default back in.
export const toAnswer = (question: ReasonQuestion, value: ReasonQuestionValue | null): ReasonQuestionAnswer => {
  return { reasonQuestionId: question.id, value: isEmptyValue(value) ? null : value };
};

// "yyyy-MM-dd" in local time, as picked.
export const formatDate = (date: Date): string => {
  const pad = (n: number) => {
    return String(n).padStart(2, '0');
  };
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
};

export const parseDate = (value: ReasonQuestionValue | null): Date | undefined => {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(value ?? ''));
  return match ? new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3])) : undefined;
};

const SERVER_ERROR = /^returnOrders\[(\d+)\]\.returnOrderLines\[(\d+)\]\.reasonQuestions\[\d+\]: (.*)$/;

// The server names the line by its position in the request; name the product instead.
export const reasonQuestionErrorMessages = (
  errors: { code: string; message: string }[] | undefined,
  returnOrders: { returnOrderLines: { salesOrderLineId: number; reasonCodes: { code: string } }[] }[],
  returns: tUpdateAndAddToReturnArgs[],
): string[] => {
  return (errors ?? [])
    .filter((error) => {
      return error.code === 'ReasonQuestionValidation';
    })
    .map((error) => {
      const match = SERVER_ERROR.exec(error.message);
      if (!match) {
        return error.message;
      }
      const line = returnOrders[Number(match[1])]?.returnOrderLines[Number(match[2])];
      const item = returns.find((returnItem) => {
        return (
          returnItem.salesOrderLineId === line?.salesOrderLineId && returnItem.returnData.reason === line?.reasonCodes.code
        );
      });
      return item ? `${item.description ?? item.productNumber}: ${match[3]}` : match[3];
    });
};
